"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

// Linha de opções da família que rola para o lado (arrastar no celular,
// setas no computador) em vez de quebrar em várias linhas (2026-10-19).
// A opção atual aparece já visível ao abrir a página.
export function FamilyOptionsScroller({ children, label }: { children: ReactNode; label: string }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanLeft(track.scrollLeft > 4);
    setCanRight(track.scrollLeft + track.clientWidth < track.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    // Opção atual centralizada na linha (sem mexer na rolagem da página).
    const center = () => {
      const current = track.querySelector<HTMLElement>("[aria-current='page']");
      if (!current) return;
      const trackBox = track.getBoundingClientRect();
      const box = current.getBoundingClientRect();
      const delta = box.left - trackBox.left - (trackBox.width - box.width) / 2;
      track.scrollTo({ left: Math.max(0, track.scrollLeft + delta), behavior: "auto" });
    };
    center();
    const frame = window.requestAnimationFrame(() => {
      center();
      update();
    });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(track);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [update]);

  function scrollBy(direction: 1 | -1) {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * Math.max(160, track.clientWidth * 0.7), behavior: "smooth" });
  }

  const arrow =
    "absolute top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-foreground shadow-sm transition-colors hover:border-secondary hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:flex";

  return (
    <div className="relative min-w-0">
      {canLeft ? (
        <button type="button" onClick={() => scrollBy(-1)} className={`${arrow} left-0`} aria-label={`Ver opções anteriores de ${label}`}>
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
      <div
        ref={trackRef}
        onScroll={update}
        className="flex snap-x gap-2 overflow-x-auto overscroll-x-contain px-1 py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
      {canLeft ? <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-background to-transparent" /> : null}
      {canRight ? <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent" /> : null}
      {canRight ? (
        <button type="button" onClick={() => scrollBy(1)} className={`${arrow} right-0`} aria-label={`Ver mais opções de ${label}`}>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
